import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Bell, Clock, Mail, MessageSquare, Loader2, Info, Eye } from 'lucide-react';
import { renderTemplate, getSampleVariables, validateTemplate, getTemplateVariables } from '@/utils/templateRenderer';
import { ReminderDispatchControl } from './ReminderDispatchControl';
// Core-100 R4 wave 3 (CORE-AZ): whether the dispatcher is actually running (its heartbeat), beside the setting.
import { ReminderDispatcherHealth } from './ReminderDispatcherHealth';

export const ReminderSettings = () => {
  const { activeShop } = useAuth();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  const [enabled, setEnabled] = useState(true);
  const [quietHoursStart, setQuietHoursStart] = useState('21:00');
  const [quietHoursEnd, setQuietHoursEnd] = useState('08:00');
  const [immediate, setImmediate] = useState(true);
  const [reminder24h, setReminder24h] = useState(true);
  const [reminder2h, setReminder2h] = useState(true);
  const [businessDayMode, setBusinessDayMode] = useState(false);
  // Core-100 R4 W4 (CORE-AZ): fallback is OFF until the shop turns it on (the column default is false too).
  const [enableFallback, setEnableFallback] = useState(false);
  const [fallbackDelayMinutes, setFallbackDelayMinutes] = useState(30);
  const [emailSubject, setEmailSubject] = useState('Reminder: {appointment_type} appointment for your {vehicle}');
  const [emailBody, setEmailBody] = useState('Hi {customer_name},\n\nThis is a reminder for your {appointment_type} appointment at {shop_name}.\n\nVehicle: {vehicle}\nService: {services}\nTime: {appointment_time}\n\nIf you need to reschedule, please contact us.\n\nThank you!');
  const [smsTemplate, setSmsTemplate] = useState('{shop_name}: Reminder for your {services} on your {vehicle}. {appointment_type} at {appointment_time}. Reply STOP to opt out.');
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    if (!activeShop?.id) return;
    fetchSettings();
  }, [activeShop?.id]);

  const fetchSettings = async () => {
    if (!activeShop?.id) return;
    
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('reminder_settings')
        .select('*')
        .eq('shop_id', activeShop.id)
        .maybeSingle();

      if (error) throw error;

      if (data) {
        setEnabled(data.reminders_enabled);
        setQuietHoursStart(data.quiet_hours_start);
        setQuietHoursEnd(data.quiet_hours_end);
        setImmediate(data.immediate_confirmation);
        setReminder24h(data.reminder_24h_enabled);
        setReminder2h(data.reminder_2h_enabled);
        setBusinessDayMode(data.business_day_mode || false);
        setEnableFallback(data.enable_fallback === true);
        setFallbackDelayMinutes(data.fallback_delay_minutes || 30);
        setEmailSubject(data.email_subject_template || emailSubject);
        setEmailBody(data.email_body_template || emailBody);
        setSmsTemplate(data.sms_template || smsTemplate);
      }
    } catch (error: any) {
      console.error('Failed to fetch reminder settings:', error);
      toast({
        title: 'Error',
        description: 'Failed to load reminder settings.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!activeShop?.id) return;

    // PHASE 3: Enhanced template validation
    const subjectValidation = validateTemplate(emailSubject);
    const bodyValidation = validateTemplate(emailBody);
    const smsValidation = validateTemplate(smsTemplate);

    // Collect all errors
    const errors: string[] = [];
    if (!subjectValidation.valid) errors.push(`Email subject: ${subjectValidation.errors.join(', ')}`);
    if (!bodyValidation.valid) errors.push(`Email body: ${bodyValidation.errors.join(', ')}`);
    if (!smsValidation.valid) errors.push(`SMS: ${smsValidation.errors.join(', ')}`);

    if (errors.length > 0) {
      toast({
        title: 'Template Validation Failed',
        description: (
          <div className="space-y-2">
            <p>Please fix the following issues:</p>
            <ul className="list-disc list-inside">
              {errors.map((err, idx) => (
                <li key={idx}>{err}</li>
              ))}
            </ul>
          </div>
        ),
        variant: 'destructive',
      });
      return;
    }

    try {
      setSaving(true);
      const { error } = await supabase
        .from('reminder_settings')
        .upsert({
          shop_id: activeShop.id,
          reminders_enabled: enabled,
          quiet_hours_start: quietHoursStart,
          quiet_hours_end: quietHoursEnd,
          immediate_confirmation: immediate,
          reminder_24h_enabled: reminder24h,
          reminder_2h_enabled: reminder2h,
          business_day_mode: businessDayMode,
          enable_fallback: enableFallback,
          fallback_delay_minutes: fallbackDelayMinutes,
          email_subject_template: emailSubject,
          email_body_template: emailBody,
          sms_template: smsTemplate,
        });

      if (error) throw error;

      toast({
        title: 'Settings Saved',
        description: 'Reminder settings and templates have been updated.',
      });
    } catch (error: any) {
      console.error('Failed to save reminder settings:', error);
      toast({
        title: 'Error',
        description: 'Failed to save reminder settings.',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  const sampleVars = getSampleVariables();
  const variables = getTemplateVariables();

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Bell className="h-5 w-5" />
            <CardTitle>Appointment Reminders</CardTitle>
          </div>
          <CardDescription>
            Configure automated email and SMS reminders with customizable message templates.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="reminders-enabled" className="text-base">
                Enable Reminders
              </Label>
              <p className="text-sm text-muted-foreground">
                Turn appointment reminders on or off for your shop
              </p>
            </div>
            <Switch
              id="reminders-enabled"
              checked={enabled}
              onCheckedChange={setEnabled}
            />
          </div>

          <Separator />

          {/* Core-100 R4 — OD-06: sending is enabled per shop only after consent capture is verified */}
          {activeShop?.id && <ReminderDispatchControl shopId={activeShop.id} />}
          {activeShop?.id && <ReminderDispatcherHealth shopId={activeShop.id} />}

          <Separator />

          <Tabs defaultValue="schedule" className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="schedule">Schedule & Timing</TabsTrigger>
              <TabsTrigger value="templates">Message Templates</TabsTrigger>
            </TabsList>

            <TabsContent value="schedule" className="space-y-6 pt-4">
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4" />
                  <Label className="text-base">Quiet Hours</Label>
                </div>
                <p className="text-sm text-muted-foreground">
                  Reminders will not be sent during these hours. Any scheduled during this window will be sent when it ends.
                </p>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="quiet-start">Start Time</Label>
                    <Input
                      id="quiet-start"
                      type="time"
                      value={quietHoursStart}
                      onChange={(e) => setQuietHoursStart(e.target.value)}
                      disabled={!enabled}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="quiet-end">End Time</Label>
                    <Input
                      id="quiet-end"
                      type="time"
                      value={quietHoursEnd}
                      onChange={(e) => setQuietHoursEnd(e.target.value)}
                      disabled={!enabled}
                    />
                  </div>
                </div>
              </div>

              <Separator />

              <div className="space-y-4">
                <Label className="text-base">Reminder Cadence</Label>
                <p className="text-sm text-muted-foreground">
                  Choose when reminders are sent to customers
                </p>
                
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label htmlFor="immediate" className="text-sm font-normal">
                        Immediate confirmation
                      </Label>
                      <p className="text-xs text-muted-foreground">
                        Send confirmation right after booking
                      </p>
                    </div>
                    <Switch
                      id="immediate"
                      checked={immediate}
                      onCheckedChange={setImmediate}
                      disabled={!enabled}
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label htmlFor="reminder-24h" className="text-sm font-normal">
                        24 hours before
                      </Label>
                      <p className="text-xs text-muted-foreground">
                        Send reminder 1 day before appointment
                      </p>
                    </div>
                    <Switch
                      id="reminder-24h"
                      checked={reminder24h}
                      onCheckedChange={setReminder24h}
                      disabled={!enabled}
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label htmlFor="reminder-2h" className="text-sm font-normal">
                        2 hours before
                      </Label>
                      <p className="text-xs text-muted-foreground">
                        Send reminder 2 hours before appointment
                      </p>
                    </div>
                    <Switch
                      id="reminder-2h"
                      checked={reminder2h}
                      onCheckedChange={setReminder2h}
                      disabled={!enabled}
                    />
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label htmlFor="business-day-mode" className="text-sm font-normal">
                        Business Day Mode
                      </Label>
                      <p className="text-xs text-muted-foreground">
                        "24h before" becomes "1 business day before" (skips weekends/holidays)
                      </p>
                    </div>
                    <Switch
                      id="business-day-mode"
                      checked={businessDayMode}
                      onCheckedChange={setBusinessDayMode}
                      disabled={!enabled || !reminder24h}
                    />
                  </div>
                </div>
              </div>

              <Separator />

              {/* SMS-2 (K71): honest copy — the backend retries the SAME
                  channel up to 3 attempts; it does NOT switch to a backup
                  channel on failure. Cross-channel fallback is not implemented,
                  so the UI no longer advertises it. */}
              <div className="space-y-4">
                <Label className="text-base">Retry on Failure</Label>
                <p className="text-sm text-muted-foreground">
                  Automatically retry the same channel if a send fails
                </p>

                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label htmlFor="enable-fallback" className="text-sm font-normal">
                      Enable Retries
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      Retry the same channel (up to 3 attempts) before marking failed
                    </p>
                  </div>
                  <Switch
                    id="enable-fallback"
                    checked={enableFallback}
                    onCheckedChange={setEnableFallback}
                    disabled={!enabled}
                  />
                </div>

                {enableFallback && (
                  <div className="space-y-2">
                    <Label htmlFor="fallback-delay">Retry Delay (minutes)</Label>
                    <Input
                      id="fallback-delay"
                      type="number"
                      min="5"
                      max="120"
                      value={fallbackDelayMinutes}
                      onChange={(e) => setFallbackDelayMinutes(parseInt(e.target.value) || 30)}
                      disabled={!enabled}
                    />
                    <p className="text-xs text-muted-foreground">
                      Wait time between retry attempts (5-120 minutes)
                    </p>
                  </div>
                )}
              </div>
            </TabsContent>

            <TabsContent value="templates" className="space-y-6 pt-4">
              <Alert>
                <Info className="h-4 w-4" />
                <AlertDescription>
                  <div className="font-medium mb-2">Available Variables:</div>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    {variables.map((v) => (
                      <div key={v.key}>
                        <code className="bg-muted px-1 rounded">{v.key}</code> - {v.description}
                      </div>
                    ))}
                  </div>
                </AlertDescription>
              </Alert>

              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <Mail className="h-4 w-4 text-muted-foreground" />
                  <Label>Email Template</Label>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="email-subject">Subject Line</Label>
                  <Input
                    id="email-subject"
                    value={emailSubject}
                    onChange={(e) => setEmailSubject(e.target.value)}
                    placeholder="Reminder: {appointment_type} appointment"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email-body">Email Body</Label>
                  <Textarea
                    id="email-body"
                    value={emailBody}
                    onChange={(e) => setEmailBody(e.target.value)}
                    rows={8}
                    placeholder="Hi {customer_name}..."
                  />
                </div>
              </div>

              <Separator />

              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <MessageSquare className="h-4 w-4 text-muted-foreground" />
                  <Label htmlFor="sms-template">SMS Template</Label>
                </div>
                <Textarea
                  id="sms-template"
                  value={smsTemplate}
                  onChange={(e) => setSmsTemplate(e.target.value)}
                  rows={3}
                  placeholder="{shop_name}: Reminder for {services}..."
                />
                <div className="text-xs text-muted-foreground">
                  SMS limit: 160 characters. Current: {smsTemplate.length}
                </div>
              </div>

              <Separator />

              <div className="space-y-3">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowPreview(!showPreview)}
                  className="w-full"
                >
                  <Eye className="mr-2 h-4 w-4" />
                  {showPreview ? 'Hide' : 'Show'} Preview
                </Button>

                {showPreview && (
                  <div className="space-y-4 p-4 border rounded-lg bg-muted/50">
                    <div>
                      <div className="font-medium mb-2">Email Preview:</div>
                      <div className="text-sm space-y-1">
                        <div><strong>Subject:</strong> {renderTemplate(emailSubject, sampleVars)}</div>
                        <div className="whitespace-pre-wrap text-muted-foreground mt-2">
                          {renderTemplate(emailBody, sampleVars)}
                        </div>
                      </div>
                    </div>
                    <div>
                      <div className="font-medium mb-2">SMS Preview:</div>
                      <div className="text-sm text-muted-foreground whitespace-pre-wrap">
                        {renderTemplate(smsTemplate, sampleVars)}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </TabsContent>
          </Tabs>

          <div className="flex justify-end pt-4 border-t">
            <Button onClick={handleSave} disabled={saving || !enabled}>
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Save Settings
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};